<template>
  <v-layout column class="content">
    <section class="team-info-container">
      <div v-if="this.skeletonLoading.teamInfo">
        <v-card class="team-info-content">
          <v-skeleton-loader class="skeleton" type="list-item-avatar-three-line, article"></v-skeleton-loader>
        </v-card>
      </div>
      <v-form v-else v-model="valid">
        <v-card class="team-info-content">
          <div class="logo">
            <v-avatar size="100" class="avatar">
              <img :src="this.avatarImage" />
              <input ref="file" id="file-avatar" type="file" name="avatar" @change="onFileChange" v-if="edit" />
            </v-avatar>
            <v-btn @click="$refs.file.click()" class="action-btn__lower smaller-font" v-if="edit">
              Upload New Image
              <v-icon>upload_file</v-icon>
            </v-btn>
          </div>
          <div class="team-size-info">
            <div>
              <h3 style="text-align: center">Players</h3>
              <bp-chart :chartdata="this.playerLimit" :key="playerLimitKey">
                <span class="team-limit-value">
                  {{ this.playerLimit.data }} /
                  {{ this.playerLimit.meta.scale }}
                </span>
              </bp-chart>
            </div>
            <div>
              <h3 style="text-align: center">Coaches</h3>
              <bp-chart :chartdata="this.coachLimit" :key="coachLimitKey">
                <span class="team-limit-value">
                  {{ this.coachLimit.data }} /
                  {{ this.coachLimit.meta.scale }}
                </span>
              </bp-chart>
            </div>
          </div>
          <v-card-title class="team-title">
            <label class="input-sizer" :data-value="currentTeam.teamName">
              <input :readonly="!edit" :class="{ editable: edit }" required v-model="currentTeam.teamName" type="text"
                onInput="this.parentNode.dataset.value = this.value" size="8" placeholder="Team Name" />
            </label>
            <select class="team-age selector" name="team-age" :disabled="!edit" id="team-age-selector"
              ref="teamAgeSelector">
              <option v-for="(type, index) in teamTypes" :key="index" value="type"
                :selected="type === currentTeam.teamAge" required>
                {{ type }}
              </option>
            </select>
            <v-card-subtitle class="team-subtitle">
              <v-icon class="inactive" :class="{ active: currentTeam.attached_license }">
                {{
                currentTeam.attached_license
                ? "check_circle"
                : "priority_high"
                }}
              </v-icon>
              License
            </v-card-subtitle>
          </v-card-title>
          <v-card-text class="general-team-info">
            <h3>Team Info</h3>
            <span class="label">Unique Pin:</span>
            <label v-if="!edit" v-clipboard:copy="currentTeam.pin"
              class="input-sizer team-info-btn v-btn v-btn--is-elevated v-btn--has-bg theme--light v-size--default"
              :data-value="currentTeam.pin" style="cursor: pointer" :class="{ editable: edit }" type="button"
              @click="handleCopyPin">
              <!-- @keyup="checkPin" -->
              <input :readonly="!edit" class="editable-btn v-btn__content" required v-model="currentTeam.pin"
                type="text" onInput="this.parentNode.dataset.value = this.value" placeholder="5 digits" maxlength="5"
                pattern="/\d{5}/" size="5" />
              <v-icon :class="this.pinCopied ? 'successfulPinCopy' : null">
                content_copy
              </v-icon>
            </label>
            <label v-else
              class="input-sizer team-info-btn v-btn v-btn--is-elevated v-btn--has-bg theme--light v-size--default"
              :data-value="currentTeam.pin" style="cursor: pointer" :class="{ editable: edit }" type="button">
              <input :readonly="!edit" class="editable-btn v-btn__content" required v-model="currentTeam.pin"
                type="text" onInput="this.parentNode.dataset.value = this.value" placeholder="5 digits" maxlength="5"
                pattern="/\d{5}/" size="5" />
              <v-icon>content_copy</v-icon>
            </label>
            <span class="label">Join ID:</span>
            <label class="input-sizer team-info-btn v-btn v-btn--is-elevated v-btn--has-bg theme--light v-size--default"
              :data-value="currentTeam.joinId" style="cursor: pointer" type="button"
              v-clipboard:copy="currentTeam.joinId" @click="handleCopyTeamId">
              <input readonly class="editable-btn v-btn__content" required v-model="currentTeam.joinId" type="text"
                onInput="this.parentNode.dataset.value = this.value" maxlength="5" size="1" />
              <v-icon :class="this.teamIdCopied ? 'successfulTeamIdCopy' : null">
                content_copy
              </v-icon>
            </label>
          </v-card-text>
          <v-card-text style="grid-area: coaches;" v-if="this.coachingStaff.length">
            <h3 style="text-align: center; color: white">Coaching staff</h3>
            <div class="coaching-staff">
              <span>
                <h4>
                  Head Coach:
                  {{ currentTeam.coachName }}
                </h4>
              </span>
              <span v-for="coach in this.coachingStaff" :key="coach.id">
                • {{ coach.firstName }} {{ coach.lastName }}
              </span>
            </div>
          </v-card-text>
          <v-card-actions class="actions">
            <!-- v-badge: content is the number, value is the on/off toggle -->
            <v-badge v-if="this.isCoach" :content="this.joinRequests" :value="this.joinRequests"
              color="rgb(255, 152, 0)" overlap>
              <v-btn class="action-btn__lower ml-0" :to="{ name: 'Join Requests' }">
                Join Requests
                <v-icon>person_add_alt_1</v-icon>
              </v-btn>
            </v-badge>
            <v-tooltip v-if="this.isCoach" left>
              <template v-slot:activator="{ on, attrs }">
                <div v-on="on">
                  <v-btn class="action-btn__lower ml-0" v-bind="attrs" disabled dark>
                    Invite New Players
                    <v-icon>mail</v-icon>
                  </v-btn>
                </div>
              </template>
              <span>Coming Soon!</span>
            </v-tooltip>

            <v-tooltip left>
              <template v-slot:activator="{ on, attrs }">
                <div v-on="on">
                  <v-btn class="action-btn__lower ml-0" v-bind="attrs" disabled dark>
                    Team Chat
                    <v-icon>chat</v-icon>
                  </v-btn>
                </div>
              </template>
              <span>Coming Soon!</span>
            </v-tooltip>
            <v-tooltip left open-delay="500" content-class="info-tooltip">
              <template v-slot:activator="{ on, attrs }">
                <div v-on="on">
                  <v-btn class="action-btn__lower ml-0" v-bind="attrs" @click="saveDefaultTeam">
                    Load by Default
                    <v-icon>offline_pin</v-icon>
                  </v-btn>
                </div>
              </template>
              <span>Selected upon login or when new browser tab opened</span>
            </v-tooltip>
            <v-btn v-if="this.isCoach" class="action-btn ml-0" @click="!edit ? (edit = !edit) : updateTeamInformation()"
              :disabled="!valid && edit" :loading="submitLoading" color="#4caf50">
              <span class="action-btn">
                <v-icon class="btn-icon btn-icon-left">edit</v-icon>
                {{ edit ? "Save" : "Edit" }}
              </span>
            </v-btn>
            <v-btn v-if="!edit && this.isCoach" @click="openDeleteTeamDialog()" color="#ff5252" class="action-btn ml-0">
              <span>
                <v-icon class="btn-icon btn-icon-left">delete</v-icon>
                Delete
              </span>
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-form>
    </section>
    <section class="team-player-list">
      <v-skeleton-loader v-if="this.skeletonLoading.teamRoster" class="skeleton"
        type="list-item-avatar-three-line@5"></v-skeleton-loader>
      <div v-else>
        <player-list :players="this.squad" />
      </div>
    </section>
    <v-dialog v-model="deleteTeamDialog" max-width="350" class="dialog-window">
      <v-card dark class="player-modal player-message-modal">
        <v-card-title class="justify-center">
          <v-spacer></v-spacer>
          <h2>Delete Team</h2>
          <v-spacer></v-spacer>
          <v-icon dark @click="deleteTeamDialog = false">close</v-icon>
        </v-card-title>
        <v-card-text>
          <div style="text-align: center">
            <div>
              <span style="font-size: smaller">
                This permanently delete the team and all the data associated
                with it. This cannot be undone. Are you sure?
              </span>
            </div>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-btn color="darken-1" rounded @click="deleteTeamDialog = false">
            Cancel
          </v-btn>
          <v-btn color="darken-1" class="v-btn--active red" rounded @click="deleteTeam()">
            OK
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="teamRequestDialog" max-width="350" class="dialog-window" dark>
      <v-card class="player-modal player-message-modal">
        <v-card-title class="justify-center">
          <v-spacer></v-spacer>
          <h2>Team Request</h2>
          <v-spacer></v-spacer>
          <v-icon dark @click="teamRequestDialog = false">close</v-icon>
        </v-card-title>
        <v-card-text>
          <div style="text-align: center">
            <div>
              <span style>
                You have pending requests to join your team. Please open the
                mobile application to accept these requests.
              </span>
            </div>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>
    <pop-up-dialog :props="{
        visible: expandTeamDialog,
        message: expandTeamMessage,
        cancelable: true,
      }" :key="toggleSwitch" @prompt-accepted="accepted" @prompt-rejected="rejected">
      <span>
        {{ costToExpandTeam }} USD/month will net you {{ memberDiff }} new
        member slots
      </span>
    </pop-up-dialog>
    <pop-up-dialog :props="{
        visible: deleteTeamSuccessDialog,
        message: 'Success\nTeam has been deleted.',
      }" :key="deleteTeamSuccessDialogKey" @prompt-accepted="closeSuccessDialog"></pop-up-dialog>
  </v-layout>
</template>
<script>
import { apiWrapper } from "../../../lib/utils"
import { logSentryError } from "@/mixins/utils.mixin.js"
import api from "@/api/firebase.api.js"
// import { getProducts, updateSubscription } from "@/api/subscription.api"
import PlayerList from "@/components/routes/squad/PlayerList.vue"
import bpChart from "../event/detail/strips/bpChart.vue"
import colors from "@/assets/sass/_variables.sass"

function debounce(timeout = 500) {
  let timer
  return (func, ...args) => {
    clearTimeout(timer)
    timer = setTimeout(() => {
      func.apply(this, args)
    }, timeout)
  }
}
const debounceEvents = debounce()
let infoTimer = null
export default {
  components: {
    PlayerList,
    bpChart,
    PopUpDialog: () => import(/* webpackPrefetch: true */ "@/components/shared/common/cards/PopUpDialog.vue"),
  },
  data() {
    return {
      playerLimitKey: false,
      coachLimitKey: 1000,
      deleteTeamDialog: false,
      deleteTeamDialogKey: false,
      deleteTeamSuccessDialog: false,
      deleteTeamSuccessDialogKey: 0,
      teamRequestDialog: false,
      toggleSwitch: false,
      expandTeamDialog: false,
      expandTeamMessage: "Subscription Update Needed\nThe following will be added to your subscription:",
      costToExpandTeam: 0,
      memberDiff: 0,
      initialPin: 0,
      teamRosterLoading: true,
      imgUrl: "",
      valid: false,
      submitLoading: false,
      edit: false,
      init: false,
      PINerrorMessages: [],
      pinCopied: false,
      teamIdCopied: false,
      pinCopyTimeout: null,
      teamIdCopyTimeout: null,
      updatePayload: {
        avatar: undefined,
        teamAge: undefined,
      },
      teamTypes: [
        "U-10",
        "U-11",
        "U-12",
        "U-13",
        "U-14",
        "U-15",
        "U-16",
        "U-17",
        "U-18",
        "U-19",
        "U-20",
        "U-21",
        "U-23",
        "College",
        "Pro",
      ],
    }
  },
  beforeDestroy() {
    this.deleteTeamSuccessDialogKey = 0
    this.playerLimitKey = false
    this.coachLimitKey = 1000
    clearTimeout(this.pinCopyTimeout)
    clearTimeout(this.teamIdCopyTimeout)
    clearTimeout(infoTimer)
  },
  mounted() {
    this.initialPin = this.currentTeam.pin
    this.updatePayload.teamAge = this.currentTeam.teamAge
    this.imgUrl = this.currentTeam?.imgUrl === "n/a" || undefined ? undefined : this.currentTeam.imgUrl

    infoTimer = setTimeout(() => {
      if (!(this.squad.length > 0)) {
        this.teamRosterLoading = false
        this.$toast.info("Unable to load the team roster. Maybe you don't have anyone on the team yet?", {
          className: "info-toaster",
        })
      }
    }, 4000)
  },
  computed: {
    isCoach() {
      return this.$store.getters["user/isCoach"]
    },
    currentTeam() {
      const team = this.$store.getters["teamList/activeTeam"]
      if (team) {
        team.initialMemberLimit = team?.initialMemberLimit ?? team?.memberLimit
      }
      return team
    },
    squad() {
      return this.$store.getters["teamList/activeTeamPlayers"]
    },
    coachingStaff() {
      return this.$store.getters["teamList/activeTeamCoaches"]
    },
    avatarImage() {
      return `${this.imgUrl || require("@/assets/icons/team_img.png").default}`
    },
    skeletonLoading() {
      let teamSize = 0
      if (this.currentTeam) {
        teamSize = Object.keys(this.currentTeam).length
      }
      return {
        teamInfo: !(teamSize > 0),
        teamRoster: !(this.squad.length > 0) && this.teamRosterLoading,
      }
    },
    playerLimit() {
      return {
        data: this.squad?.length ?? 0,
        color: { color: colors["bright-green"] },
        meta: {
          showPercent: false,
          scale: this.currentTeam?.default_player_slots ?? 30 + this.currentTeam?.additional_player_slots ?? 0,
          precision: 0,
        },
      }
    },
    coachLimit() {
      return {
        data: this.coachingStaff.length ?? 1,
        color: { color: colors["bg-orange"] },
        meta: {
          showPercent: false,
          scale: this.currentTeam?.default_coach_slots ?? 3 + this.currentTeam?.additional_coach_slots ?? 0,
          precision: 0,
        },
      }
    },
    joinRequests() {
      if (typeof this.currentTeam?.joinRequests !== "string") {
        return Object.keys(this.currentTeam?.joinRequests).length ?? 0
      }
      return 0
    },
  },

  methods: {
    saveDefaultTeam() {
      this.$store.dispatch("teamList/setDefaultTeam", {
        teamId: this.currentTeam.id,
      })
    },
    handleCopyPin() {
      this.pinCopied = true
      this.pinCopyTimeout = setTimeout(() => {
        this.pinCopied = false
      }, 1000)
    },
    handleCopyTeamId() {
      this.teamIdCopied = true
      this.teamIdCopyTimeout = setTimeout(() => {
        this.teamIdCopied = false
      }, 1000)
    },
    async accepted() {
      console.log("prompt accepted")
      // TODO finish the update team limit function
      // console.log(await getProducts())
      // updateSubscription({
      //   action: "updateTeamMemberLimit",
      //   id: item.subscriptionItemId,
      //   teamLimit: item.teamLimit,
      //   email: item.email,
      // })
      //   .then(() => {
      //     this.showToastr("success", "Team Limits update successfully!")
      //     const updatedSub = this.subscriptions.find((sub) => {
      //       // console.log("looking for sub", item.subscriptionItemId, sub.item.id)
      //       return sub.item.id === item.subscriptionItemId
      //     })
      //     updatedSub.item.teamLimit = item.teamLimit
      //     this.itemToUpdate.teamLimit = this.itemToUpdate.oldLimit =
      //       item.teamLimit
      //     this.color = "success"
      //     // console.log(updatedSub)
      //   })
      //   .catch((error) => {
      //     if (this.itemToUpdate && this.itemToUpdate.teamLimit) {
      //       this.itemToUpdate.teamLimit = this.itemToUpdate?.oldLimit ?? 0
      //     }
      //     this.showToastr(
      //       "error",
      //       "Error: unable to update the team limit. " + error.message
      //     )
      //   })
      //   .finally(() => {
      //     this.confirmDialog = false
      //   })
    },
    rejected() {
      this.currentTeam.memberLimit = this.currentTeam.initialMemberLimit
      this.memberDiff = 0
      this.costToExpandTeam = 0
    },
    reduceMemberLimit() {
      if (this.currentTeam.memberLimit - 5 < 30) {
        this.$toast.error("Minimum possible member limit reached")
        return
      }
      this.currentTeam.memberLimit -= 5
    },
    increaseMemberLimit() {
      if (this.currentTeam.memberLimit + 5 > 60) {
        this.$toast.error("Maximum possible member limit reached")
        return
      }
      this.currentTeam.memberLimit += 5
      this.openDialog(this.currentTeam)
    },
    async openDialog(_event) {
      // debounce clicks
      debounceEvents.bind(this)(async () => {
        this.costToExpandTeam = this.memberDiff = this.currentTeam.memberLimit - this.currentTeam.initialMemberLimit
        this.expandTeamDialog = true
        this.toggleSwitch = !this.toggleSwitch

        // const limitInfo = await subscriptionApi.getTeamLimitInfo(team.id)
        // const plan = this.plans.find((plan) => plan.id === item.teamLimitPlan)
        // // const plan = _.find(this.plans, { id: item.teamLimitPlan });
        // this.itemToUpdate = item
        // this.itemToUpdate.oldLimit = limitInfo.limit

        // const additionalTeams = item.teamLimit - this.itemToUpdate.oldLimit

        // if (additionalTeams > 0) {
        //   this.confirmDialog = true

        //   item.additionalTeamPrice = this.formatCurrency(
        //     additionalTeams * plan.amount
        //   )
        // }
      })
    },
    onFileChange(event) {
      this.readURL(event.target)
    },
    readURL(input) {
      if (input.files?.[0]) {
        const file = input.files[0]
        const reader = new FileReader()
        reader.onload = (event) => {
          this.imgUrl = event.target.result
          this.updatePayload.avatar = file
        }
        reader.readAsDataURL(input.files[0])
      }
    },

    async updateTeamInformation() {
      if (!this.valid) {
        return
      }
      const selectedIndex = this.$refs.teamAgeSelector.selectedIndex
      const payload = {
        teamAge: this.$refs.teamAgeSelector[selectedIndex].text,
        teamName: this.currentTeam.teamName,
        pin: this.currentTeam.pin,
        storedPin: this.initialPin,
      }

      this.submitLoading = true
      const report = await apiWrapper(api.updateTeamInformation, [
        this.$store.state.user.current.uid,
        this.currentTeam.id,
        payload,
        this.updatePayload.avatar,
      ])
      if (report) {
        this.submitLoading = false
        this.$store.dispatch("user/load", this.$store.state.user.current.uid)
        this.edit = false
      }
    },
    openDeleteTeamDialog() {
      this.deleteTeamDialog = true
    },
    async deleteTeam() {
      this.$store.commit("setOverlay", true)
      const isCoach = String(this.$store?.state?.user?.profile?.coachOrPlayer).toLowerCase() === "coach"
      const team = (await api.deleteTeam(this.currentTeam.id, isCoach)) || {}
      this.$store.commit("setOverlay", false)

      if (!team || team?.error) {
        this.$toast.error("Error: unable to delete the team.")
        logSentryError(team.error, "error", {
          message: team.error,
          teamId: this.currentTeam.id,
          userId: this.$store.state.user.current.uid,
          fn: "deleteTeam via manageSquad.vue",
        })
      } else {
        this.deleteTeamSuccessDialog = true
        clearTimeout(infoTimer)
      }
      this.deleteTeamSuccessDialogKey++
      this.deleteTeamDialog = false
      this.deleteTeamDialogKey = !this.deleteTeamDialogKey
    },
    forceUpdate() {
      this.$forceUpdate()
    },
    async closeSuccessDialog() {
      this.deleteTeamSuccessDialogKey++
      this.deleteTeamSuccessDialog = false
      const switchToTeam = this.$store.state.teamList.teams.find((team) => team.id !== this.currentTeam.id)
      if (switchToTeam) {
        await this.$store.dispatch("teamList/selectTeam", switchToTeam.id)
      }
      await this.$store.dispatch("teamList/load", this.$store.state.user.current.uid)
      this.$router.push("/")
    },
  },
}
</script>
<style lang="sass" scoped>
@import '@/assets/sass/globals.sass'
.info-tooltip
  background-color: $bright-blue
  opacity: 1 !important

.successfulPinCopy, .successfulTeamIdCopy
  color: $bright-green !important

.editable
  border: 2px solid white !important
  border-radius: 4px !important
  background: #424242 !important
  color: white !important
  cursor: text !important

  & > input
    cursor: text !important

.skeleton
  & ::v-deep *
    background: transparent !important
.smaller-font
  font-size: 0.7rem !important

.select-from-dropdown
  & ::v-deep .v-input__slot
    background-color: hsla(0,0%,100%,.16) !important
  & ::v-deep .v-select__selection--comma
    color: white !important

.player-message-modal
  background-color: #424242 !important
  border-radius: 20px !important

  .v-card__actions
    display: flex
    justify-content: space-around
    width: 100%
    & *
      flex-grow: 1


.content
  overflow-y: scroll
  display: flex
  flex-grow: 1
  color: white
  padding: 1rem
  width: 100%

  .team-info-container
    margin: 0 -1rem



    .team-info-content
      padding: 0.5rem
      display: grid
      grid-template-columns: minmax(100px, 1fr) 1fr 1fr  1fr
      column-gap: 1rem
      grid-template-rows: 50% 50%
      grid-template-areas: "logo title info actions" "graph coaches coaches actions"
      background: transparent
      color: white

      .team-size-info
        grid-area: graph
        display: grid
        place-content: center
        grid-auto-flow: column
        column-gap: 0.5rem

        & ::v-deep .circle
          display: flex
          align-content: center
          justify-content: center

        & ::v-deep .legend-text
          display: none
        // & ::v-deep .team-limit-value
        span
          justify-self: center


      .action-btn, .team-info-btn, .action-btn__lower
        width: 8rem !important

      .editable-btn
        text-align: center
        cursor: pointer

      .actions
        display: grid
        grid-area: actions
        justify-content: center
        align-content: start
        row-gap: 0.5rem


      .action-btn
        color: white

        &__lower
          & ::v-deep span.v-btn__content
            width: inherit
            white-space: normal
            font-size: smaller

      .logo
        grid-area: logo
        display: grid
        justify-items: center
        align-content: start
        // padding-top: 25px
        .avatar
          margin-bottom: 1rem

          .avatar-image
            width: 150px
            height: 150px
            background: center center no-repeat
            background-size: cover
            border-radius: 50%
            padding-top: 100%
            position: relative
            width: 100%
            height: 0
          input
            cursor: pointer
            width: 100%
            height: 100%
            opacity: 0
      .slider-selector
        grid-area: selector
        align-self: center

      .team-title
        padding: 0
        // padding-top: 25px
        grid-area: title
        justify-self: center
        align-content: center
        display: block

        .input-sizer
          display: inline-grid
          vertical-align: top
          align-items: center
          position: relative
          padding: .25em .5em

          &::after, input
            color: white
            width: auto
            min-width: 1em
            grid-area: 1 / 2
            font: inherit
            padding: 0.25rem
            margin: 0
            resize: none
            background: none
            appearance: none
            border: none

          &::after
            content: attr(data-value)
            white-space: pre-wrap
            visibility: hidden

        .team-age
          border: 2px solid white
          border-radius: 100%
          display: inline-block
          padding: 0.25rem
          text-align: center
          color: white
          -webkit-appearance: caret
          -moz-appearance: caret
          -o-appearance: caret
          appearance: caret
          background-color: #424242

          &:disabled
            background-color: transparent
            border: 2px solid white !important
            opacity: 1
            border-radius: 100%
            padding: 0.25rem
            display: inline-block
            text-align: center
            color: white !important
            -webkit-appearance: none
            -moz-appearance: none
            -o-appearance: none
            appearance: none

          option
            background-color: #424242
            margin: 0
            cursor: pointer
            border-width: 0
            opacity: 1

          // &:hover

        .team-subtitle
          padding: 0 !important

          .inactive
            color: $bg-orange
          .active
            color: $bright-green

      .general-team-info, .coaching-staff
        color: white
        display: grid
        justify-items: center
        // padding-top: 25px

      .general-team-info
        grid-area: info
      .coaching-staff
        // grid-area: coaches
        display: grid
        span
          justify-self: start
          text-transform: capitalize


      .action-btns
        display: grid
        place-content: center
        grid-area: actions
        row-gap: 0.5rem

.label
  font-weight: bold


.defaultTeamImage
  height: 100%
  min-width: 150px

.rewards-link
  color: $white

.join-id
  text-align: center
  padding-top: 20px

.btn-icon
  font-size: 1.2em
  &.btn-icon-left
    padding-right: .5em
  &.btn-icon-right
    padding-left: .5em
</style>
